/**
 * The URL state of `/conversations` (hub §4.2).
 *
 * `session` + `message` name one answer and `env` says which database it is
 * in; `node` says which step of that answer's trace has its detail open, so
 * the browser's back button closes it. With all three the screen skips the
 * list and opens that message's trace — which is what makes the TC drawer's
 * "원본 대화 Trace" a link rather than an instruction to go and find it.
 * Without `message` the conversation opens with no answer picked.
 *
 * `q` and `email` are the session list's filters. They stay in the address
 * while a conversation is open, so back from it lands on the same list.
 *
 * Normalization, not validation: a hand-edited or stale address lands on the
 * lookup screen, which says what was wrong with it, instead of throwing.
 */

import { canonicalSessionId } from "./sessionLookup";

export type TraceEnvironment = "dev" | "prod";

export type ConversationsSearch = {
  /** Explicit evaluation owner; omitted for product source conversations. */
  store?: "sams";
  purpose?: import("@/types/issuePipeline").RunPurpose | "all";
  env: TraceEnvironment;
  /** Session to open directly; null means "show the lookup". */
  session: string | null;
  /** Message whose trace to open. Only acted on together with `session`. */
  message: string | null;
  /**
   * Which node of that trace has its detail open.
   *
   * Here rather than in component state so **back closes it** — the same
   * reason `message` is here. The detail renders below the whole chart, so
   * the bar that opened it can be off screen, and back is the dismissal every
   * reader already has. A trace step becomes linkable as a side effect.
   *
   * A `TraceNode.id` (`tb:<turn>:0`, `cw:<uuid>`, …), derived from the trace,
   * so a stale one selects nothing rather than mis-selecting.
   */
  node: string | null;
  /** Session list: session_id, name or member, matched by the server. */
  q: string | null;
  /** Session list: one member's email, matched exactly. */
  email: string | null;
};

export const CONVERSATIONS_DEFAULT_SEARCH: ConversationsSearch = {
  env: "dev",
  session: null,
  message: null,
  node: null,
  q: null,
  email: null,
};

export const CONVERSATIONS_ROUTE = "/conversations" as const;

/** Trimmed, and null when nothing is left. */
function asText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/**
 * An id in its canonical spelling when it is one, otherwise as given — the
 * screen reports what is wrong with a non-id rather than dropping it.
 */
function asId(value: unknown): string | null {
  const text = asText(value);
  return text && (canonicalSessionId(text) ?? text);
}

export function parseConversationsSearch(
  raw: Record<string, unknown>,
): ConversationsSearch {
  // Anything other than `prod` is dev — including absent. Guessing prod from
  // a typo would point every request at the other database.
  return {
    ...(raw.store === "sams" ? { store: "sams" as const } : {}),
    purpose: ["production", "test", "reference", "unknown", "all"].includes(String(raw.purpose)) ? raw.purpose as ConversationsSearch["purpose"] : "all",
    env: raw.env === "prod" ? "prod" : "dev",
    session: asId(raw.session),
    message: asId(raw.message),
    // Not an id in the session sense, so `asId`'s canonicalization would be
    // wrong here — a node id is an opaque string from the trace.
    node: asText(raw.node),
    q: asText(raw.q),
    // The server lowercases before its exact match, so the address does too.
    email: asText(raw.email)?.toLowerCase() ?? null,
  };
}
